const DEFAULT_LOCALE = 'en-GB'
const DETAIL_PREVIEW_LENGTH = 180

// `stageKey` points at the same `general.*` label the Kanban uses for each
// column, so a status-change row reads "Moved to {stage}" with the real stage
// name instead of a hardcoded phrase. `titleKey` is only used for the initial
// `application_created` row. `paused` has no Kanban column, so it keeps a
// dedicated phrase and intentionally omits `stageKey`.
const STATUS_CONFIG = {
	pending: {
		titleKey: 'application-history.events.application-pending',
		stageKey: 'general.requested',
		icon: ['far', 'clock'],
		tone: 'blue'
	},
	requested: {
		titleKey: 'application-history.events.application-requested',
		stageKey: 'general.requested',
		icon: ['far', 'clock'],
		tone: 'blue'
	},
	invitation: {
		// Talent-pool invites surface as an `application_created` row (the
		// JobOfferInvitation notification is deduped against it), so the
		// "+ invitation email sent" copy lives in the title — one line, same
		// format as the declined row.
		titleKey: 'application-history.events.invited-to-apply-talent-pool-with-invitation-email',
		stageKey: 'general.invited',
		icon: ['far', 'paper-plane'],
		tone: 'blue'
	},
	accepted: {
		titleKey: 'general.accepted',
		stageKey: 'general.accepted',
		icon: ['far', 'arrow-right'],
		tone: 'blue',
		markerIcon: 'arrow-right'
	},
	interview: {
		titleKey: 'general.interview',
		stageKey: 'general.interview',
		icon: ['far', 'calendar-alt'],
		tone: 'blue',
		markerIcon: 'calendar'
	},
	hired: {
		titleKey: 'application-history.events.hired',
		stageKey: 'general.hired',
		icon: ['far', 'check-circle'],
		tone: 'green',
		markerIcon: 'hired'
	},
	denied: {
		titleKey: 'application-history.events.application-declined',
		stageKey: 'general.denied',
		icon: ['far', 'times-circle'],
		tone: 'red',
		markerIcon: 'declined'
	},
	paused: {
		titleKey: 'application-history.events.application-paused',
		icon: ['far', 'hourglass'],
		tone: 'gray'
	}
}

const NOTIFICATION_CONFIG = {
	UserAppliedToJobOffer: {
		titleKey: 'application-history.events.applied',
		icon: ['far', 'user'],
		tone: 'gray',
		preferApplicantAvatar: true
	},
	ApplicantApplicationSubmitted: {
		titleKey: 'application-history.events.applied',
		icon: ['far', 'user'],
		tone: 'gray',
		preferApplicantAvatar: true
	},
	JobOfferInvitation: {
		titleKey: 'application-history.events.invited-to-apply-talent-pool-with-invitation-email',
		icon: ['far', 'paper-plane'],
		tone: 'blue',
		markerIcon: 'paper-plane'
	},
	ApplicationRejected: {
		// A surviving ApplicationRejected row means the notification was actually
		// dispatched (channels: database + mail), so it carries the same
		// email-sent title as the deduped status_changed → denied row.
		titleKey: 'application-history.events.application-declined-with-rejection-email',
		icon: ['far', 'times-circle'],
		tone: 'red',
		markerIcon: 'declined'
	},
	OrganizationAnsweredApplication: {
		titleKey: 'application-history.events.organization-answered-application',
		icon: ['far', 'reply'],
		tone: 'blue'
	},
	ApplicantAnsweredInvitation: {
		titleKey: 'application-history.events.applicant-answered-invitation',
		icon: ['far', 'user'],
		tone: 'gray',
		preferApplicantAvatar: true
	}
}

const CANDIDATE_NOTIFICATION_STATUS_TITLE_KEYS = {
	denied:
		'application-history.events.application-declined-with-rejection-email',
	interview:
		'application-history.events.moved-to-interview-with-invitation-email'
}

// Mirrors the backend's $applicantEngagedStatuses
// (ApplicationStatusTransitionPolicy::isTalentPoolDismissal). The rejection
// email only goes out when an organization denies a candidate who was actually
// engaged with the offer — denials from any other origin (talent-pool
// dismissals, applicant withdrawals) send no email, so they must not claim one.
const APPLICANT_ENGAGED_STATUSES = [
	'pending',
	'accepted',
	'invitation',
	'interview',
	'hired'
]

function candidateNotificationEmailSent(event, metadata) {
	if (metadata.to_status === 'denied') {
		return (
			event.actor_type === 'organization' &&
			APPLICANT_ENGAGED_STATUSES.includes(metadata.from_status)
		)
	}

	if (metadata.to_status === 'interview') {
		return Boolean(metadata.invitation_email_sent)
	}

	return false
}

function translate(t, key, params = undefined) {
	if (typeof t !== 'function') {
		return key
	}

	return t(key, params)
}

function toDate(value) {
	if (!value) {
		return null
	}

	const date = value instanceof Date ? value : new Date(value)

	return Number.isNaN(date.getTime()) ? null : date
}

function startOfDay(date) {
	return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function isSameDay(first, second) {
	return startOfDay(first).getTime() === startOfDay(second).getTime()
}

function isYesterday(date, now) {
	const yesterday = startOfDay(now)
	yesterday.setDate(yesterday.getDate() - 1)

	return startOfDay(date).getTime() === yesterday.getTime()
}

function createDateTimeFormatter(locale, options) {
	try {
		return new Intl.DateTimeFormat(locale || DEFAULT_LOCALE, options)
	} catch (error) {
		return new Intl.DateTimeFormat(DEFAULT_LOCALE, options)
	}
}

function formatTimeLabel(date, locale = DEFAULT_LOCALE) {
	return createDateTimeFormatter(locale, {
		hour: '2-digit',
		minute: '2-digit',
		hour12: false
	})
		.format(date)
		.replace(/^24:/, '00:')
}

function formatAbsoluteDate(date, options) {
	if (typeof options.formatDate === 'function') {
		const formattedDate = options.formatDate(date, 'dd MMM yyyy')

		if (formattedDate) {
			return formattedDate
		}
	}

	return createDateTimeFormatter(options.locale, {
		day: '2-digit',
		month: 'short',
		year: 'numeric'
	}).format(date)
}

function getDateGroup(date, options) {
	const now = options.now || new Date()

	if (isSameDay(date, now)) {
		return {
			key: dateKey(date),
			label: translate(options.t, 'application-history.date.today')
		}
	}

	if (isYesterday(date, now)) {
		return {
			key: dateKey(date),
			label: translate(options.t, 'application-history.date.yesterday')
		}
	}

	return {
		key: dateKey(date),
		label: formatAbsoluteDate(date, options)
	}
}

function dateKey(date) {
	const year = date.getFullYear()
	const month = String(date.getMonth() + 1).padStart(2, '0')
	const day = String(date.getDate()).padStart(2, '0')

	return `${year}-${month}-${day}`
}

function stringifyMetadata(metadata) {
	try {
		return JSON.stringify(metadata || {})
	} catch (error) {
		return ''
	}
}

function stripHtml(value) {
	if (!value) {
		return ''
	}

	return String(value)
		.replace(/<[^>]*>/g, ' ')
		.replace(/&nbsp;/g, ' ')
		.replace(/&amp;/g, '&')
		.replace(/&lt;/g, '<')
		.replace(/&gt;/g, '>')
		.replace(/&quot;/g, '"')
		.replace(/&#039;/g, "'")
		.replace(/\s+/g, ' ')
		.trim()
}

function compactName(...parts) {
	return parts.filter(Boolean).join(' ').trim()
}

function actorNameFor(event, config, options) {
	const actor = event.actor || {}
	const metadata = event.metadata || {}
	const fullName =
		actor.name ||
		actor.full_name ||
		actor.fullName ||
		compactName(
			actor.firstname || actor.first_name,
			actor.lastname || actor.last_name
		) ||
		event.actor_name ||
		metadata.actor_name

	if (fullName) {
		return fullName
	}

	if (event.actor_type === 'applicant' || config?.preferApplicantAvatar) {
		return (
			options.applicantName ||
			translate(options.t, 'application-history.actor.applicant')
		)
	}

	if (event.actor_type === 'organization') {
		return translate(options.t, 'application-history.actor.organization')
	}

	return translate(options.t, 'application-history.actor.system')
}

function actorAvatarFor(event, config, options) {
	const actorAvatar = event.actor?.avatar?.url || event.actor?.avatar_url || null

	if (actorAvatar) {
		return actorAvatar
	}

	if (
		event.actor_type === 'applicant' ||
		(config && config.preferApplicantAvatar)
	) {
		return options.applicantAvatarUrl || null
	}

	return null
}

function usesActorAvatarFor(event, config) {
	if (event.actor?.avatar?.url || event.actor?.avatar_url) {
		return true
	}

	return (
		event.actor_type === 'applicant' ||
		Boolean(config && config.preferApplicantAvatar)
	)
}

function eventConfigFor(event, options = {}) {
	const metadata = event.metadata || {}

	if (event.type === 'application_created') {
		return (
			STATUS_CONFIG[metadata.status] || {
				titleKey: 'application-history.events.applied',
				icon: ['far', 'user'],
				tone: 'gray',
				preferApplicantAvatar: true
			}
		)
	}

	if (event.type === 'status_changed') {
		if (
			event.actor_type === 'applicant' &&
			metadata.from_status === 'invitation' &&
			metadata.to_status === 'accepted'
		) {
			return {
				titleKey: 'application-history.events.accepted-invitation',
				icon: ['far', 'check-circle'],
				tone: 'blue',
				preferApplicantAvatar: true
			}
		}

		const base = STATUS_CONFIG[metadata.to_status]

		if (base && base.stageKey) {
			const candidateNotificationStatusTitleKey =
				CANDIDATE_NOTIFICATION_STATUS_TITLE_KEYS[metadata.to_status]

			if (
				candidateNotificationStatusTitleKey &&
				candidateNotificationEmailSent(event, metadata)
			) {
				return {
					...base,
					titleKey: candidateNotificationStatusTitleKey
				}
			}

			return {
				...base,
				titleKey: 'application-history.events.moved-to-stage',
				titleParams: {
					stage: translate(options.t, base.stageKey)
				}
			}
		}

		if (base) {
			return base
		}

		return {
			titleKey: 'application-history.events.status-changed',
			icon: ['far', 'sync'],
			tone: 'blue',
			titleParams: {
				status: metadata.to_status || translate(options.t, 'general.unknown')
			}
		}
	}

	if (['applicant_profile_deactivated', 'applicant_profile_reactivated'].includes(event.type)) {
		return {
			titleKey: `application-history.events.${event.type.replace(/_/g, '-')}`,
			icon: ['far', 'user'],
			tone: 'gray',
			preferApplicantAvatar: true
		}
	}

	if (event.type === 'notification_created') {
		return (
			NOTIFICATION_CONFIG[metadata.notification_type] || {
				titleKey: 'application-history.events.notification-created',
				icon: ['far', 'bell-plus'],
				tone: 'blue'
			}
		)
	}

	if (event.type === 'comment_added') {
		return {
			titleKey: 'application-history.events.note-added',
			icon: ['far', 'comment'],
			tone: 'blue',
			markerIcon: 'comment'
		}
	}

	return {
		titleKey: 'application-history.events.unknown',
		icon: ['far', 'question-circle'],
		tone: 'gray'
	}
}

function detailsFor(event) {
	const metadata = event.metadata || {}

	return stripHtml(
		event.details ||
			metadata.details ||
			metadata.comment ||
			metadata.comment_body ||
			metadata.preview ||
			''
	)
}

function noteContentFor(event) {
	if (event.type !== 'comment_added') {
		return ''
	}

	const metadata = event.metadata || {}

	return (
		metadata.comment_body ||
		metadata.details ||
		metadata.comment ||
		event.details ||
		''
	)
}

function isHistoryNoteEdited(event) {
	const metadata = event.metadata || {}

	if (typeof metadata.is_edited === 'boolean') {
		return metadata.is_edited
	}

	if (!event.created_at || !metadata.updated_at) {
		return false
	}

	const createdAt = new Date(event.created_at).getTime()
	const updatedAt = new Date(metadata.updated_at).getTime()

	if (Number.isNaN(createdAt) || Number.isNaN(updatedAt)) {
		return false
	}

	return updatedAt > createdAt
}

export function normalizeApplicationHistoryEvents(events = [], options = {}) {
	const locale = options.locale || DEFAULT_LOCALE
	const groups = []
	const groupsByKey = {}

	events
		.map((event, index) => normalizeApplicationHistoryEvent(event, index, {
			...options,
			locale
		}))
		.filter(Boolean)
		.sort((first, second) => second.timestamp - first.timestamp)
		.forEach(event => {
			if (!groupsByKey[event.dateGroup.key]) {
				groupsByKey[event.dateGroup.key] = {
					key: event.dateGroup.key,
					label: event.dateGroup.label,
					events: []
				}
				groups.push(groupsByKey[event.dateGroup.key])
			}

			groupsByKey[event.dateGroup.key].events.push(event)
		})

	return groups
}

export function normalizeApplicationHistoryEvent(event, index = 0, options = {}) {
	if (!event || typeof event !== 'object') {
		return null
	}

	const date = toDate(event.created_at)

	if (!date) {
		return null
	}

	const config = eventConfigFor(event, options)
	const details = detailsFor(event)
	const baseTitle = translate(options.t, config.titleKey, config.titleParams)
	const title =
		event.type === 'comment_added' && isHistoryNoteEdited(event)
			? `${baseTitle} ${translate(options.t, 'application-history.edited-marker')}`
			: baseTitle

	return {
		key:
			event.id ||
			`${event.type || 'activity'}-${event.created_at}-${event.actor_type || ''}-${stringifyMetadata(
				event.metadata
			)}-${index}`,
		type: event.type,
		timestamp: date.getTime(),
		dateGroup: getDateGroup(date, options),
		title,
		isEdited: event.type === 'comment_added' && isHistoryNoteEdited(event),
		timeLabel: formatTimeLabel(date, options.locale || DEFAULT_LOCALE),
		actorName: actorNameFor(event, config, options),
		actorAvatarUrl: actorAvatarFor(event, config, options),
		usesActorAvatar: usesActorAvatarFor(event, config),
		icon: config.icon,
		markerIcon: config.markerIcon,
		tone: config.tone,
		details,
		noteContent: noteContentFor(event),
		previewDetails:
			details.length > DETAIL_PREVIEW_LENGTH
				? `${details.slice(0, DETAIL_PREVIEW_LENGTH).trim()}…`
				: details,
		canExpand: details.length > DETAIL_PREVIEW_LENGTH
	}
}

export { DETAIL_PREVIEW_LENGTH }
